(() => {
  // ui/two-soul/model.js
  var INTENTIONS = Object.freeze(["closeness", "space", "clarity", "courage"]);
  var PAIRS = Object.freeze(["closeness_closeness", "closeness_space", "closeness_clarity", "closeness_courage", "space_space", "space_clarity", "space_courage", "clarity_clarity", "clarity_courage", "courage_courage"]);
  function pairKey(a, b) {
    if (!INTENTIONS.includes(a) || !INTENTIONS.includes(b)) return null;
    return [a, b].sort((x, y) => INTENTIONS.indexOf(x) - INTENTIONS.indexOf(y)).join("_");
  }
  var sharedArcana = (a, b) => (a + b) % 22;
  var cleanName = (value) => String(value ?? "").normalize("NFC").replace(/[\u0000-\u001f\u007f-\u009f\u202a-\u202e\u2066-\u2069]/g, "").trim().replace(/\s+/g, " ");
  var validName = (value) => typeof value === "string" && Array.from(value).length > 0 && Array.from(value).length <= 20;
  function drawArcana(cryptoSource = globalThis.crypto) {
    const sample = new Uint32Array(1), limit = Math.floor(4294967296 / 22) * 22;
    do {
      cryptoSource.getRandomValues(sample);
    } while (sample[0] >= limit);
    return sample[0] % 22;
  }
  var card = (value) => /^(?:[0-9]|1[0-9]|2[01])$/.test(value ?? "");
  var payloadKeys = ["v", "h", "c", "p", "d", "i", "j"];
  function parseLink(input) {
    const url = new URL(input), fragment = url.hash.slice(1);
    const legacy = !fragment && (url.searchParams.has("h") || url.searchParams.has("c"));
    if (!fragment && !legacy) return null;
    if (fragment.length > 1200) throw Error("Link length");
    if (fragment) decodeURIComponent(fragment.replace(/\+/g, " "));
    const p = legacy ? url.searchParams : new URLSearchParams(fragment);
    for (const key of payloadKeys) if (p.getAll(key).length > 1) throw Error("Duplicate field");
    if (!legacy) {
      for (const key of p.keys()) if (!payloadKeys.includes(key)) throw Error("Unknown field");
    }
    const version = legacy ? p.get("v") || "1" : p.get("v");
    if (!["1", "2"].includes(version)) throw Error("Version");
    if (version === "1" && (p.has("i") || p.has("j"))) throw Error("Version fields");
    const person = (nameKey, cardKey, intentKey) => {
      const raw = p.get(nameKey), name = cleanName(raw), id = p.get(cardKey), intention = p.get(intentKey);
      if (!raw || Array.from(raw).length > 20 || !validName(name) || !card(id)) throw Error("Invalid participant");
      if (intention !== null && !INTENTIONS.includes(intention)) throw Error("Invalid intention");
      return Object.freeze({ name, id: Number(id), intention });
    };
    const host = person("h", "c", "i");
    const complete = p.has("p") || p.has("d");
    if (!complete && p.has("j")) throw Error("Orphan intention");
    const partner = complete ? person("p", "d", "j") : null;
    return { version: Number(version), host, partner };
  }
  function makeLink(input, host, partner = null, currentTheme = null) {
    const url = new URL(input), old = url.searchParams, q = new URLSearchParams();
    if (!/^https?:$/.test(url.protocol)) throw Error("Invalid base URL");
    if (old.get("view") === "two-soul-resonance" || !/\/pages\/two-soul-resonance\/?$/.test(url.pathname)) q.set("view", "two-soul-resonance");
    if (/^\d+$/.test(old.get("preview_theme_id") || "")) q.set("preview_theme_id", old.get("preview_theme_id"));
    if (!q.has("preview_theme_id") && ["unpublished", "development"].includes(currentTheme?.role) && /^\d+$/.test(String(currentTheme?.id || ""))) q.set("preview_theme_id", String(currentTheme.id));
    if (["0", "1"].includes(old.get("pb"))) q.set("pb", old.get("pb"));
    if (/^[a-zA-Z0-9_-]{1,100}$/.test(old.get("revision") || "")) q.set("revision", old.get("revision"));
    const hash = new URLSearchParams({ v: "2", h: host.name, c: String(host.id) });
    if (host.intention) hash.set("i", host.intention);
    if (partner) {
      hash.set("p", partner.name);
      hash.set("d", String(partner.id));
      if (partner.intention) hash.set("j", partner.intention);
    }
    url.search = q.toString();
    url.hash = hash.toString();
    parseLink(url.href);
    return url.href;
  }

  // ui/two-soul/poster.js
  var loadImage = (src) => new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = "anonymous";
    const timer = setTimeout(() => reject(Error("Image timeout")), 1e4);
    image.onload = () => {
      clearTimeout(timer);
      resolve(image);
    };
    image.onerror = () => {
      clearTimeout(timer);
      reject(Error("Image unavailable"));
    };
    image.src = src;
  });
  function wrap(ctx, value, width) {
    const words = String(value).split(/\s+/), rows = [];
    let row = "";
    for (const word of words) {
      const next = row ? row + " " + word : word;
      if (ctx.measureText(next).width <= width) {
        row = next;
        continue;
      }
      if (row) rows.push(row);
      row = "";
      if (ctx.measureText(word).width > width) {
        for (const char of Array.from(word)) {
          if (ctx.measureText(row + char).width > width && row) {
            rows.push(row);
            row = "";
          }
          row += char;
        }
      } else row = word;
    }
    if (row) rows.push(row);
    return rows;
  }
  function drawStar(ctx, x, y, r) {
    ctx.beginPath();
    for (let i = 0; i < 16; i++) {
      const a = i * Math.PI / 8 - Math.PI / 2, rr = i % 2 ? r * 0.2 : r;
      const xx = x + Math.cos(a) * rr, yy = y + Math.sin(a) * rr;
      i ? ctx.lineTo(xx, yy) : ctx.moveTo(xx, yy);
    }
    ctx.closePath();
    ctx.fill();
  }
  async function createKeepsake({ host, partner, card: card2, theme, copy }) {
    const [image] = await Promise.all([loadImage(card2.image), document.fonts?.ready ? Promise.race([document.fonts.ready, new Promise((r) => setTimeout(r, 2e3))]) : Promise.resolve()]);
    const canvas = document.createElement("canvas");
    canvas.width = 1080;
    let ctx = canvas.getContext("2d");
    if (!ctx) throw Error("Canvas unavailable");
    const blocks = [];
    function text(value, x, y2, width, font, step, color = "#f3ebdd", align = "center") {
      ctx.font = font;
      const rows = wrap(ctx, value, width);
      blocks.push({ rows, x, y: y2, font, step, color, align });
      return y2 + rows.length * step;
    }
    const ui = '"Lora",sans-serif', serif = '"Lora",serif';
    text("THE WORLD OF THE FOOL", 540, 76, 920, "400 26px " + ui, 36, "#c9a45c");
    text(copy.title, 540, 144, 940, "400 52px " + serif, 64);
    for (const [person, x] of [[host, 292], [partner, 788]]) {
      text(person.name, x, 490, 420, "400 36px " + serif, 44);
      text(person.intention ? copy[person.intention] : copy.legacy_label, x, 580, 420, "400 27px " + ui, 36, "#d8ba7c");
    }
    let y = text(theme.theme, 540, 660, 900, "400 54px " + serif, 66);
    y = text(copy.question_label.toLocaleUpperCase(), 540, y + 25, 900, "400 22px " + ui, 30, "#c9a45c");
    y = text(theme.question, 540, y + 20, 860, "400 35px " + serif, 47);
    y = text(theme.ritual, 540, y + 25, 860, "400 25px " + ui, 36, "#d4c9d8");
    const mirrorY = Math.max(1070, y + 45);
    text(copy.mirror_label.toLocaleUpperCase(), 350, mirrorY + 25, 600, "400 20px " + ui, 28, "#c9a45c", "left");
    text(card2.id + " \xB7 " + card2.name, 350, mirrorY + 72, 620, "400 34px " + serif, 43, "#f3ebdd", "left");
    const end = text(card2.essence, 350, mirrorY + 124, 620, "400 25px " + ui, 34, "#d4c9d8", "left");
    const footerY = Math.max(mirrorY + 220, end + 45);
    text("worldofthefool.com", 540, footerY, 900, "400 23px " + ui, 34, "#c9a45c");
    canvas.height = Math.max(1350, Math.ceil(footerY + 75));
    ctx = canvas.getContext("2d");
    ctx.fillStyle = "#120d1b";
    ctx.fillRect(0, 0, 1080, canvas.height);
    ctx.strokeStyle = "#796037";
    ctx.lineWidth = 1;
    ctx.strokeRect(32, 32, 1016, canvas.height - 64);
    for (const cx of [340, 740]) {
      ctx.strokeStyle = cx === 340 ? "#b99650" : "#b7a68e";
      for (const r of [142, 133, 99]) {
        ctx.beginPath();
        ctx.arc(cx, 310, r, 0, Math.PI * 2);
        ctx.stroke();
      }
      ctx.fillStyle = cx === 340 ? "#c9a45c" : "#c4b397";
      ctx.beginPath();
      ctx.arc(cx, 310, 32, 0, Math.PI * 2);
      ctx.stroke();
      for (let i = 0; i < 16; i++) {
        const a = i * Math.PI / 8;
        ctx.beginPath();
        ctx.moveTo(cx + Math.cos(a) * 40, 310 + Math.sin(a) * 40);
        ctx.lineTo(cx + Math.cos(a) * 61, 310 + Math.sin(a) * 61);
        ctx.stroke();
      }
    }
    ctx.fillStyle = "#c9a45c";
    drawStar(ctx, 540, 310, 25);
    for (const block of blocks) {
      ctx.font = block.font;
      ctx.fillStyle = block.color;
      ctx.textAlign = block.align;
      block.rows.forEach((row, i) => ctx.fillText(row, block.x, block.y + i * block.step));
    }
    const ratio = Math.min(110 / image.naturalWidth, 165 / image.naturalHeight), w = image.naturalWidth * ratio, h = image.naturalHeight * ratio;
    ctx.drawImage(image, 190 + (110 - w) / 2, mirrorY + (165 - h) / 2, w, h);
    const blob = await new Promise((resolve, reject) => {
      const timer = setTimeout(() => reject(Error("PNG timeout")), 12e3);
      canvas.toBlob((b) => {
        clearTimeout(timer);
        b ? resolve(b) : reject(Error("PNG unavailable"));
      }, "image/png");
    });
    return blob;
  }

  // ui/two-soul/app.js
  function mount(root) {
    if (root.dataset.resonanceReady) return;
    const $ = (s) => root.querySelector(s), $$ = (s) => [...root.querySelectorAll(s)];
    let copy, deck;
    const entities = { amp: "&", quot: '"', apos: "'", lt: "<", gt: ">", nbsp: " ", "#39": "'", "#x27": "'" };
    const translatedJSON = (node) => JSON.parse(node.textContent, (_key, value) => typeof value === "string" ? value.replace(/&(amp|quot|apos|lt|gt|nbsp|#39|#x27);/g, (_match, key) => entities[key]) : value);
    try {
      copy = translatedJSON($("[data-resonance-copy]"));
      deck = translatedJSON($("[data-resonance-deck]"));
      if (deck.length !== 22 || deck.some((c, i) => c.id !== i || !c.name || !c.image) || typeof copy !== "object" || !copy.rules) throw Error("Data");
    } catch {
      $("[data-status]").textContent = "The tarot symbols could not load. Please refresh the page.";
      return;
    }
    root.dataset.resonanceReady = "true";
    const controller = new AbortController(), on = (el, event, fn) => el.addEventListener(event, fn, { signal: controller.signal });
    const reduced = matchMedia("(prefers-reduced-motion:reduce)"), compact = matchMedia("(max-width:800px)");
    let host = null, partner = null, mode = "first", epoch = 0, busy = false, shareBusy = false, downloadBusy = false, animations = [], finishReveal = null, visible = true;
    const form = $("[data-person-form]"), input = $("[data-person-name]"), status = $("[data-status]");
    const nav = document.querySelector("#JourneyNavSummary-header-free-tools"), previousNav = nav?.getAttribute("aria-current");
    if (nav) nav.setAttribute("aria-current", "page");
    const text = (key, vars = {}) => Object.entries(vars).reduce((s, [k, v]) => s.replaceAll("{" + k + "}", String(v)), copy[key] || key);
    const set = (selector, value) => $(selector).textContent = value;
    const themeFor = () => copy.rules[pairKey(host?.intention, partner?.intention)] || { theme: copy.legacy_theme, explanation: copy.legacy_explanation, question: copy.legacy_question, ritual: copy.legacy_ritual };
    function announce(key, action = false) {
      $(action ? "[data-action-status]" : "[data-status]").textContent = text(key);
    }
    function cancel() {
      epoch++;
      for (const a of animations) a.cancel();
      animations = [];
      finishReveal = null;
      busy = false;
      root.removeAttribute("aria-busy");
    }
    function fade(node, duration = 220) {
      if (reduced.matches || document.hidden) return;
      const a = node.animate([{ opacity: 0.3 }, { opacity: 1 }], { duration, easing: "ease-out" });
      animations.push(a);
      a.finished.catch(() => {
      });
    }
    function ownLink() {
      return host ? makeLink(location.href, host, partner, globalThis.Shopify?.theme) : null;
    }
    function commit(push = true) {
      const url = host ? ownLink() : (() => {
        const u = new URL(location.href);
        u.hash = "";
        for (const k of ["v", "h", "c", "p", "d", "i", "j"]) u.searchParams.delete(k);
        return u.href;
      })();
      history[push ? "pushState" : "replaceState"]({ ...history.state, twoSoul: { mode } }, "", url);
    }
    function inputSelection() {
      return form.querySelector("[data-intention]:checked")?.value || null;
    }
    function orbit() {
      const selected = inputSelection(), name = cleanName(input.value), current = mode === "partner" ? "b" : "a";
      for (const [side, person] of [["a", host], ["b", partner]]) {
        const pending = !person && side === current && (mode === "first" || mode === "partner"), choice = person?.intention || (pending ? selected : null), g = $("[data-sky=" + side + "]");
        g.classList.toggle("is-selected", Boolean(pending && choice));
        g.classList.toggle("is-ready", Boolean(person));
        set("[data-person-" + side + "]", person?.name || (pending && name ? name : copy[side === "a" ? "you" : "them"]));
        set("[data-intention-" + side + "]", choice ? copy[choice] : person ? copy.legacy_label : "");
        $("[data-intention-" + side + "]").hidden = !choice && !person;
        set("[data-state-" + side + "]", person ? copy[mode === "result" ? "complete" : "ready"] : pending && choice ? copy.selected : copy[pending ? "choose" : "waiting"]);
      }
    }
    function steps() {
      const current = mode === "first" ? 1 : mode === "result" ? 3 : mode === "revealing" ? 3 : 2;
      for (const el of $$("[data-step]")) {
        const n = Number(el.dataset.step);
        el.toggleAttribute("aria-current", n === current);
        if (n === current) el.setAttribute("aria-current", "step");
        el.querySelector("[data-step-number]").textContent = n < current ? "\u2713" : String(n).padStart(2, "0");
      }
    }
    function setImage(img, card2) {
      img.removeAttribute("data-failed");
      img.src = card2.image;
      img.alt = card2.name;
    }
    function imageError(event) {
      const img = event.target;
      if (img.tagName === "IMG") {
        img.dataset.failed = "true";
        $("[data-image-error]").hidden = false;
      }
    }
    function story() {
      const shared = deck[sharedArcana(host.id, partner.id)], fragment = document.createDocumentFragment();
      for (const [person, card2, label] of [[host, deck[host.id], host.name], [null, shared, copy.shared_card], [partner, deck[partner.id], partner.name]]) {
        const figure = document.createElement("figure"), img = document.createElement("img"), caption = document.createElement("figcaption"), p = document.createElement("p"), h = document.createElement("h4"), essence = document.createElement("p");
        img.width = 400;
        img.height = 600;
        img.loading = "lazy";
        setImage(img, card2);
        p.textContent = label;
        h.textContent = card2.id + " \xB7 " + card2.name;
        essence.textContent = card2.essence;
        caption.append(p, h, essence);
        figure.append(img, caption);
        fragment.append(figure);
      }
      $("[data-story-cards]").replaceChildren(fragment);
      set("[data-formula]", `${host.id} + ${partner.id} = ${host.id + partner.id} \u2192 ${shared.id} \xB7 ${shared.name}`);
    }
    function legacyFields() {
      const container = $("[data-legacy-fields]");
      container.replaceChildren();
      for (const [side, person] of [["a", host], ["b", partner]]) {
        const field = document.createElement("fieldset"), legend = document.createElement("legend"), choices = document.createElement("div");
        legend.textContent = person.name;
        choices.className = "ts-choices";
        for (const value of INTENTIONS) {
          const label = document.createElement("label"), radio = document.createElement("input"), dot = document.createElement("span"), name = document.createElement("span");
          radio.type = "radio";
          radio.name = "legacy-" + side + "-" + root.id;
          radio.value = value;
          radio.required = true;
          radio.checked = person.intention === value;
          dot.className = "ts-choice-dot";
          dot.setAttribute("aria-hidden", "true");
          name.textContent = copy[value];
          label.append(radio, dot, name);
          choices.append(label);
        }
        field.dataset.legacySide = side;
        field.append(legend, choices);
        container.append(field);
      }
    }
    function render() {
      root.dataset.state = mode;
      steps();
      orbit();
      $(".ts-forms").hidden = mode === "result";
      form.hidden = !["first", "partner", "revealing"].includes(mode);
      $("[data-invite]").hidden = mode !== "invite";
      $("[data-result]").hidden = mode !== "result";
      $("[data-mirror]").hidden = mode !== "result";
      $("[data-conversion]").hidden = mode !== "result";
      $("[data-intro-strip]").hidden = mode === "result";
      $("[data-reset-box]").hidden = mode === "first";
      const second = mode === "partner" || mode === "revealing";
      set("[data-form-heading]", second ? cleanName(input.value) ? text("partner_named", { name: cleanName(input.value) }) : copy.partner_heading : copy.need_heading);
      set("[data-form-helper]", copy[second ? "partner_helper" : "begin_helper"]);
      set("[data-submit]", copy[second ? "reveal" : "begin"]);
      $("[data-ready-note]").hidden = !second;
      set("[data-ready-note]", second ? text(host.intention ? "host_ready" : "host_symbol_ready", { name: host.name }) : "");
      $("[data-different]").hidden = !second;
      $("[data-remote-hint]").hidden = second;
      input.disabled = busy;
      $$("[data-intention]").forEach((el) => el.disabled = busy);
      $("[data-submit]").disabled = busy;
      if (host) set("[data-invite-name]", host.name + " \xB7 " + (host.intention ? copy[host.intention] : copy.legacy_label));
      set("[data-subtitle]", mode === "result" ? text("shared_reflection", { a: host.name, b: partner.name }) : copy.subtitle);
      if (mode === "result") {
        const theme = themeFor(), card2 = deck[sharedArcana(host.id, partner.id)];
        for (const [field, key] of [["theme", "theme"], ["explanation", "explanation"], ["question", "question"], ["small-ritual", "ritual"]]) set("[data-" + field + "]", theme[key]);
        setImage($("[data-mirror-image]"), card2);
        set("[data-mirror-title]", card2.id + " \xB7 " + card2.name);
        set("[data-mirror-essence]", card2.essence);
        story();
        const missing = !pairKey(host.intention, partner.intention);
        $("[data-add-intentions]").hidden = !missing;
        if (missing) legacyFields();
      }
    }
    function clearErrors() {
      for (const el of [input, form.querySelector("fieldset")]) el.removeAttribute("aria-invalid");
      $("[data-name-error]").hidden = true;
      $("[data-intention-error]").hidden = true;
    }
    function resetForm() {
      form.reset();
      clearErrors();
    }
    function restore() {
      cancel();
      host = null;
      partner = null;
      resetForm();
      $("[data-manual]").hidden = true;
      $("[data-story]").open = false;
      $("[data-add-intentions]").open = false;
      status.textContent = "";
      set("[data-action-status]", "");
      try {
        const parsed = parseLink(location.href);
        if (!parsed) {
          mode = "first";
        } else {
          host = parsed.host;
          partner = parsed.partner;
          mode = partner ? "result" : history.state?.twoSoul?.mode === "invite" ? "invite" : "partner";
        }
      } catch {
        mode = "first";
        announce("invalid");
      }
      render();
    }
    function reveal() {
      cancel();
      const token = epoch;
      mode = "revealing";
      busy = true;
      render();
      commit();
      root.setAttribute("aria-busy", "true");
      announce("revealing");
      const finish = () => {
        if (epoch !== token) return;
        for (const a of animations) a.cancel();
        animations = [];
        finishReveal = null;
        busy = false;
        root.removeAttribute("aria-busy");
        mode = "result";
        render();
        commit(false);
        status.textContent = "";
        fade($("[data-result]"), 180);
        $("[data-theme]").focus({ preventScroll: true });
      };
      finishReveal = finish;
      if (reduced.matches || document.hidden || !visible) {
        finish();
        return;
      }
      try {
        const a = $("[data-travel-a]").animate([{ transform: "translateX(0)", opacity: 0 }, { opacity: 1, offset: 0.15 }, { transform: "translateX(62px)", opacity: 1 }], { duration: 520, easing: "cubic-bezier(.16,1,.3,1)", fill: "forwards" });
        const b = $("[data-travel-b]").animate([{ transform: "translateX(0)", opacity: 0 }, { opacity: 1, offset: 0.15 }, { transform: "translateX(-62px)", opacity: 1 }], { duration: 520, easing: "cubic-bezier(.16,1,.3,1)", fill: "forwards" });
        animations.push(a, b);
        Promise.all([a.finished, b.finished]).then(() => {
          if (epoch !== token) return;
          const star = $("[data-shared-star]").animate([{ opacity: 0.2 }, { opacity: 1 }], { duration: 180, fill: "forwards" });
          animations.push(star);
          return star.finished;
        }).then(() => {
          if (epoch === token) finishReveal?.();
        }).catch(() => {
          if (epoch === token) finishReveal?.();
        });
      } catch {
        finish();
      }
    }
    on(form, "submit", (event) => {
      event.preventDefault();
      if (busy || !["first", "partner"].includes(mode)) return;
      clearErrors();
      const name = cleanName(input.value), intention = inputSelection();
      if (!validName(name)) {
        set("[data-name-error]", copy.name_error);
        $("[data-name-error]").hidden = false;
        input.setAttribute("aria-invalid", "true");
        input.focus();
        return;
      }
      if (!intention) {
        set("[data-intention-error]", copy.intention_error);
        $("[data-intention-error]").hidden = false;
        form.querySelector("fieldset").setAttribute("aria-invalid", "true");
        $("[data-intention]").focus();
        return;
      }
      input.value = name;
      const person = Object.freeze({ name, intention, id: drawArcana() });
      if (!host) {
        host = person;
        mode = "invite";
        render();
        commit();
        $("[data-invite-heading]").focus({ preventScroll: true });
        fade($("[data-invite]"));
      } else {
        partner = person;
        reveal();
      }
    });
    on(input, "input", () => {
      clearErrors();
      orbit();
      if (mode === "partner") set("[data-form-heading]", cleanName(input.value) ? text("partner_named", { name: cleanName(input.value) }) : copy.partner_heading);
    });
    for (const radio of $$("[data-intention]")) on(radio, "change", () => {
      clearErrors();
      orbit();
    });
    on($("[data-together]"), "click", () => {
      if (!host || partner) return;
      resetForm();
      mode = "partner";
      render();
      commit();
      fade(form);
      input.focus({ preventScroll: true });
    });
    async function share(which) {
      if (!host || shareBusy || busy || which === "result" && !partner) return;
      const token = epoch, url = which === "result" ? makeLink(location.href, host, partner, globalThis.Shopify?.theme) : makeLink(location.href, host, null, globalThis.Shopify?.theme), button = $("[data-share-" + which + "]");
      shareBusy = true;
      button.disabled = true;
      $("[data-manual]").hidden = true;
      announce("sharing", which === "result");
      try {
        let native = false;
        if (typeof navigator.share === "function") {
          try {
            await navigator.share({ title: copy.title, url });
            native = true;
          } catch (error) {
            if (error.name === "AbortError") {
              if (token === epoch) announce("share_cancelled", which === "result");
              return;
            }
          }
        }
        if (token !== epoch) return;
        if (!native) {
          try {
            if (!navigator.clipboard?.writeText) throw Error("Clipboard");
            await navigator.clipboard.writeText(url);
            if (token === epoch) announce("copied", which === "result");
          } catch {
            if (token !== epoch) return;
            const field = $("[data-manual-url]");
            field.value = url;
            $("[data-manual]").hidden = false;
            field.focus();
            field.select();
            field.setSelectionRange(0, url.length);
            announce("manual", which === "result");
          }
        } else if (token === epoch) {
          $(which === "result" ? "[data-action-status]" : "[data-status]").textContent = "";
        }
      } finally {
        shareBusy = false;
        button.disabled = false;
      }
    }
    on($("[data-share-invite]"), "click", () => share("invite"));
    on($("[data-share-result]"), "click", () => share("result"));
    on($("[data-download]"), "click", async () => {
      if (!host || !partner || downloadBusy || mode !== "result") return;
      const token = epoch, a = { ...host }, b = { ...partner }, card2 = deck[sharedArcana(a.id, b.id)], theme = { ...themeFor() }, button = $("[data-download]");
      downloadBusy = true;
      button.disabled = true;
      button.textContent = copy.saving;
      set("[data-action-status]", "");
      try {
        const blob = await createKeepsake({ host: a, partner: b, card: card2, theme, copy });
        if (token !== epoch) return;
        const url = URL.createObjectURL(blob), link = document.createElement("a");
        link.href = url;
        link.download = "two-soul-resonance.png";
        document.body.append(link);
        link.click();
        link.remove();
        setTimeout(() => URL.revokeObjectURL(url), 3e4);
        announce("saved", true);
      } catch {
        if (token === epoch) announce("save_error", true);
      } finally {
        downloadBusy = false;
        button.disabled = false;
        button.textContent = copy.save;
      }
    });
    on($("[data-legacy-form]"), "submit", (event) => {
      event.preventDefault();
      if (!host || !partner) return;
      const a = $("[data-legacy-side=a] input:checked")?.value, b = $("[data-legacy-side=b] input:checked")?.value;
      if (!INTENTIONS.includes(a) || !INTENTIONS.includes(b)) return;
      host = Object.freeze({ ...host, intention: a });
      partner = Object.freeze({ ...partner, intention: b });
      $("[data-add-intentions]").open = false;
      render();
      commit();
      fade($("[data-result]"));
    });
    on($("[data-reset]"), "click", () => {
      cancel();
      host = null;
      partner = null;
      mode = "first";
      resetForm();
      $("[data-manual]").hidden = true;
      status.textContent = "";
      set("[data-action-status]", "");
      render();
      commit();
      input.focus({ preventScroll: true });
    });
    on(root, "error", imageError);
    root.addEventListener("error", imageError, { capture: true, signal: controller.signal });
    const details = () => {
      for (const side of ["a", "b"]) {
        const target = $("[data-detail-target=" + side + "]");
        target.replaceChildren();
        if (!compact.matches) target.append($("[data-orbit-detail=" + side + "]").content.querySelector("g").cloneNode(true));
      }
    };
    details();
    on(compact, "change", details);
    on(window, "popstate", restore);
    on(window, "hashchange", restore);
    on(reduced, "change", () => {
      if (reduced.matches) finishReveal?.();
    });
    on(document, "visibilitychange", () => {
      if (document.hidden) finishReveal?.();
    });
    const observer = new IntersectionObserver((entries) => {
      visible = entries[0].isIntersecting;
      if (!visible) finishReveal?.();
    });
    observer.observe(root);
    on(window, "pagehide", () => {
      finishReveal?.();
      for (const a of animations) a.cancel();
      animations = [];
    });
    on(document, "shopify:section:unload", (event) => {
      if (event.target.contains(root)) {
        cancel();
        controller.abort();
        observer.disconnect();
        if (nav) {
          previousNav ? nav.setAttribute("aria-current", previousNav) : nav.removeAttribute("aria-current");
        }
      }
    });
    for (const el of form.querySelectorAll("[disabled]")) el.disabled = false;
    restore();
  }
  var boot = () => document.querySelectorAll("[data-resonance-root]").forEach(mount);
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot, { once: true });
  else boot();
  document.addEventListener("shopify:section:load", boot);
})();
